<template>
  <PhotoRequestCancellationModal
    v-if="isCancellationModalOpen"
    ref="photo-cancellation-modal"
    v-model="isCancellationModalOpen"
    :outbound-request="outboundRequest"
    @back-to-photo-requests="backToPhotoRequests()"
    @on-refetch-outbound-photo="$emit('onRefetchOutboundPhoto')"
  />
  <SoDialog
    v-else
    v-model="value"
    :card-title="existingRequestModalTitle(outboundRequest)"
    :max-width="395"
    @on-close="resetModal()"
  >
    <template v-if="!emailSendingSuccess">
      <SoStatus
        v-if="isFirstOrSecondEmailSent(outboundRequest)"
        ref="request-photo-status-modal"
        :status="existingChipModalStatus"
        class="mb-4 d-inline-block"
      />
      <p class="mb-4 photo-request-dialog__text">
        Freshdesk ticket:
        <SoLink
          :text="outboundRequest.freshdeskTicketId || ''"
          :href="
            externalRoutes.FreshdeskTicket.path +
            outboundRequest.freshdeskTicketId
          "
          target="blank"
          class="text-primary text-decoration-none"
        />
      </p>
      <p class="mb-4 photo-request-dialog__text">
        Meter type: {{ outboundRequest.fuel }}
      </p>
      <p class="mb-4 photo-request-dialog__text">
        Reason: {{ outboundRequest.requestReason }}
      </p>
      <p class="mb-4 photo-request-dialog__text">
        Team assigned: {{ outboundRequest.team }}
      </p>
      <SoCheckbox
        v-if="!isFollowUpFeatureHidden(outboundRequest)"
        ref="follow-up-checkbox"
        class="pb-4"
        data-testid="follow-up-checkbox"
        :disabled="true"
        :label="followUpCheckboxLabel(outboundRequest.status)"
      />
      <SoLink
        ref="open-cancel-btn"
        data-testid="open-cancel-btn"
        text="Cancel Photo Request"
        @click="cancelPhotoRequest"
      />
      <SoAlert
        v-if="emailSendingError"
        ref="errorMessage"
        class="mt-5"
        type="error"
      >
        {{ errorMessage }}
      </SoAlert>
      <div
        class="w-100"
        :class="{
          'd-flex justify-end': !isFollowUpFeatureHidden(outboundRequest),
        }"
      >
        <SoButton
          class="mt-8"
          title="close"
          large
          :type="
            isFollowUpFeatureHidden(outboundRequest) ? 'primary' : 'secondary'
          "
          :full-width="isFollowUpFeatureHidden(outboundRequest)"
          @click="resetModal()"
        />
        <SoButton
          v-if="!isFollowUpFeatureHidden(outboundRequest)"
          ref="follow-up-btn"
          :disabled="true"
          class="mt-8 ml-4"
          title="Send Follow-up"
          large
          type="primary"
          :loading="isLoading"
          data-testid="follow-up-btn"
          @click="sendFollowUpEmail(outboundRequest)"
        />
      </div>
    </template>
    <template v-else>
      <div class="title-text mt-n4">
        <v-icon icon="check-circle success-icon mr-1" size="x-large"></v-icon>
        <h4 class="d-inline success-title">Follow-up Email Sent</h4>
      </div>
      <SoButton
        class="mt-8"
        title="close"
        large
        type="primary"
        full-width
        @click="resetModal()"
      />
    </template>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoLink from "@/components/SoLink.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import PhotoRequestCancellationModal from "../modals/PhotoRequestCancellationModal.vue"
  import { externalRoutes } from "@/constants/routes/externalRoutes"
  import { MeterReadingsMutator } from "@/mutators/meterReadings/MeterReadingsMutator"
  import { Status } from "@/types/Status"
  import { errorHandler, errorReporter } from "@/services/ErrorService"
  import { OutboundFollowUpResponse } from "@/api/backofficeRest/generated/model"

  export default defineComponent({
    components: {
      SoButton,
      SoCheckbox,
      SoDialog,
      SoLink,
      SoStatus,
      SoAlert,
      PhotoRequestCancellationModal,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      outboundRequest: {
        type: Object,
        default: () => ({}),
        required: true,
      },
      existingChipModalStatus: {
        type: Object as PropType<Status>,
        default: () => ({}),
        required: true,
      },
    },
    emits: ["update:modelValue", "onRefetchOutboundPhoto"],
    data() {
      return {
        isLoading: false,
        emailSendingSuccess: false,
        emailSendingError: false,
        externalRoutes,
        sendFollowUpEmailRequest:
          MeterReadingsMutator.sendFollowUpEmailForOutboundRequest(),
        errorMessage: "",
        defaultErrorMessage: "There was an error with processing your request.",
        isCancellationModalOpen: false,
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
    methods: {
      existingRequestModalTitle(outboundRequest) {
        return this.emailSendingSuccess
          ? ""
          : `Photo Request ${outboundRequest?.createdAt}`
      },
      followUpCheckboxLabel(status) {
        if (status === "FIRST_FOLLOW_UP_SENT") {
          return "Send second follow-up email"
        } else {
          return "Send follow-up email"
        }
      },
      isFirstOrSecondEmailSent(outboundRequest) {
        return (
          outboundRequest.status === "FIRST_FOLLOW_UP_SENT" ||
          outboundRequest.status === "SECOND_FOLLOW_UP_SENT"
        )
      },
      isFollowUpFeatureHidden(outboundRequest) {
        return (
          outboundRequest.status === "CANCELLED" ||
          outboundRequest.status === "EMAIL_SENT" ||
          outboundRequest.status === "REQUESTED" ||
          outboundRequest.status === "UPLOADED" ||
          outboundRequest.status === "SECOND_FOLLOW_UP_SENT"
        )
      },
      sendFollowUpEmail(outboundRequest) {
        this.isLoading = true
        this.emailSendingSuccess = false
        this.emailSendingError = false

        this.sendFollowUpEmailRequest
          .mutateAsync({
            currentStatus: outboundRequest.status,
            outboundRequestId: outboundRequest.outboundRequestId,
          })
          .then((response) => {
            const responseMessage = (response as OutboundFollowUpResponse)
              ?.message

            if (responseMessage === "success") {
              this.emailSendingSuccess = true
            } else {
              this.handleSubmissionError(responseMessage)
            }
          })
          .catch((error) => {
            this.handleSubmissionError(error)
          })
          .finally(() => {
            this.isLoading = false
          })
      },
      handleSubmissionError(error) {
        if (typeof error === "string") {
          this.errorMessage = error
        } else if (error instanceof Error) {
          this.errorMessage = errorHandler.userMessage(
            error,
            "Failed to send the follow up email",
          )
        } else {
          this.errorMessage = ""
        }
        if (!this.errorMessage) {
          // error not found
          this.errorMessage = this.defaultErrorMessage
        }

        errorReporter.report(new Error(this.errorMessage))
        this.emailSendingError = true
      },
      cancelPhotoRequest() {
        this.value = false
        this.isCancellationModalOpen = true
      },
      backToPhotoRequests() {
        this.isCancellationModalOpen = false
        this.value = true
      },
      resetModal() {
        if (this.emailSendingSuccess) {
          this.$emit("onRefetchOutboundPhoto")
        }
        this.emailSendingSuccess = false
        this.emailSendingError = false
        this.value = false
      },
    },
  })
</script>

<style scoped>
  .photo-request-dialog__text {
    font-size: 18px;
    line-height: 28px;
  }
  .success-icon {
    font-size: 26px;
    line-height: 28px;
    vertical-align: middle;
    color: rgb(var(--v-theme-success));
  }
  .success-title {
    vertical-align: middle;
  }
</style>
